import React, { useEffect, useRef, useState } from "react";
import { POWERUPS } from "../../shared/powerups";
import PowerUpTokenIcon from "../../shared/PowerUpTokenIcon";
import Icon from "./Icon";
import { MAP_SIZES, mapValueOf, shapeAndSizeOf } from "./maps";
import {
  AMOUNTS, MODES, POWERUP_SETS, SPEEDS, START, applyFlavour, autoSize, flavourInfo, flavourOf, isSet, lenScore, modeOfConfig, roundLen, scoreOf, sizeName, sizeOf,
  type FlavourId, type Len, type Rules, type Shape, type Size, type Spawn, type Tri,
} from "./roomRules";
import { B, Chev } from "./RoomsUI";
import r from "./roomPage.module.scss";

// The room's rules as rows you press (Game Rooms and the local game): each row
// says its value large, with a drawing of it on the right, in the value's own
// colour; pressing it drops the choices down right under it (nothing dimmed),
// and a press outside closes them. A guest sees the same rows, read only.

// Every value its own colour, so a glance says what is set.
const SPEED_C = ["#4caf50", "#ffd23f", "#ff5c5c"];             // slow, normal, fast
const AMOUNT_C = ["#8ea3bd", "#4fa8ff", "#4caf50", "#ff9f45", "#ff3b5c"]; // off, rare, normal, frequent, chaos
const SHAPE_C: Record<Shape, string> = { rectangle: "#28c4d4", stadium: "#b06cff", square: "#ff9f45", circle: "#ff6ad5", surprise: "#e9f3ff" };
const WIN_C = { score: "#ffd23f", rounds: "#4fa8ff" };
const SHAPE_LABEL: Record<Shape, string> = { stadium: "Stadium", rectangle: "Rectangle", square: "Square", circle: "Circle", surprise: "Surprise me" };
const LEN_LABEL: Record<Len, string> = { short: "Short", normal: "Normal", long: "Long" };
const RESPAWN_C = { on: "#19f0b0", off: "#8ea3bd" };

// ---------- icons ----------
const sv = { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round" as const, strokeLinejoin: "round" as const, "aria-hidden": true };
const IMap = () => <svg {...sv}><rect x="3" y="6" width="18" height="12" rx="6" /></svg>;
const IWin = () => <svg {...sv}><path d="M7 4h10v5a5 5 0 01-10 0V4zM4 5h3M17 5h3M4 5c0 3 1.5 4.5 3 4.5M20 5c0 3-1.5 4.5-3 4.5M12 14v4M8 20h8" /></svg>;
const ISpeed = () => <svg {...sv}><path d="M4 16a8 8 0 1116 0M12 16l4-5" /></svg>;
const ISpawn = () => <svg {...sv}><circle cx="12" cy="13" r="7" /><path d="M12 13V9M10 3h4" /></svg>;
const IMode = () => <svg {...sv}><path d="M4 7h9M17 7h3M4 17h3M11 17h9" /><circle cx="15" cy="7" r="2" /><circle cx="9" cy="17" r="2" /></svg>;
const IBolt = () => <svg {...sv}><path d="M13 3L5 13.5h6L10 21l8-10.5h-6L13 3z" /></svg>;
const IRespawn = () => <svg {...sv}><path d="M20 12a8 8 0 11-2.4-5.7M20 4v5h-5" /></svg>;

// ---------- the drawings ----------
// A map's outline alone (no trails in it): a 2:1 rectangle or stadium, a square, a circle.
function ShapeIcon({ shape }: { shape: Shape }) {
  const wide = shape === "rectangle" || shape === "stadium";
  return (
    <svg className={r.shapeIcon} viewBox={wide ? "0 0 60 32" : "0 0 32 32"} fill="none" aria-hidden="true">
      {shape === "surprise" ? <rect x="2" y="2" width="56" height="28" rx="14" stroke="currentColor" strokeWidth="2.4" strokeDasharray="5 5" />
        : wide ? <rect x="2" y="2" width="56" height="28" rx={shape === "stadium" ? 14 : 3} stroke="currentColor" strokeWidth="2.4" />
        : <rect x="2" y="2" width="28" height="28" rx={shape === "circle" ? 14 : 3} stroke="currentColor" strokeWidth="2.4" />}
    </svg>
  );
}
const Steps = ({ n, of }: { n: number; of: number }) => (
  <span className={r.steps}>{Array.from({ length: of }, (_, k) => <i key={k} className={k < n ? r.lit : ""} style={{ height: 6 + k * (14 / Math.max(1, of - 1)) }} />)}</span>
);
const Chevrons = ({ n }: { n: number }) => (
  <span className={r.chevrons}>{[0, 1, 2].map((i) => <svg key={i} viewBox="0 0 10 16" className={i <= n ? r.lit : ""} aria-hidden="true"><path d="M2 2l6 6-6 6" /></svg>)}</span>
);

// What a row needs to show, read from the host's Rules or (a guest) from the
// host's live config.
export interface RuleView {
  shape: Shape; size: Size; sizeKey: string; win: "score" | "rounds"; score: number; rounds: number;
  speed: Tri; amount: Spawn; pu: string[]; flavour: FlavourId; respawn: boolean;
}
export function viewOfRules(x: Rules): RuleView {
  return { shape: x.shape, size: x.size, sizeKey: sizeOf(x), win: x.win, score: scoreOf(x), rounds: x.rounds, speed: x.speed, amount: x.amount, pu: x.pu, flavour: flavourOf(x), respawn: x.respawn };
}
const nearestIndex = (list: { value: number }[], v: number) => list.reduce((best, o, i) => (Math.abs(o.value - v) < Math.abs(list[best].value - v) ? i : best), 0);
// A guest's view: the host's live config.
export function viewOfConfig(c: any, players: number): RuleView {
  const a = c && c.map ? shapeAndSizeOf(mapValueOf(c.map)) : null;
  const disabled = new Set<string>((c && c.disabledPowerUps) || []);
  const rounds = c && c.maxRounds ? c.maxRounds : 0;
  const mode = modeOfConfig(c || {});
  const respawn = !!(c && c.respawn);
  return {
    shape: (a ? a.shape : "stadium") as Shape,
    size: (a ? a.size : autoSize(players)) as Size,
    sizeKey: a ? a.size : autoSize(players),
    win: rounds ? "rounds" : "score",
    score: c && typeof c.maxScore === "number" ? c.maxScore : lenScore(players, "normal", respawn),
    rounds: rounds || roundLen("normal", respawn),
    speed: nearestIndex(SPEEDS, c && typeof c.wormSpeed === "number" ? c.wormSpeed : SPEEDS[1].value) as Tri,
    amount: nearestIndex(AMOUNTS, c && typeof c.powerUpSpawnChance === "number" ? c.powerUpSpawnChance : AMOUNTS[2].value) as Spawn,
    pu: POWERUPS.map((p) => p.key).filter((k) => !disabled.has(k)),
    flavour: mode === "custom" ? "custom" : mode,
    respawn,
  };
}

type Key = "map" | "respawn" | "win" | "speed" | "mode" | "spawn" | "powerups";
// Each rule's own colour for its icon, as the old setup's section headings had them.
const LABEL_C: Record<Key, string> = { map: "#28c4d4", respawn: "#19f0b0", win: "#f5c542", speed: "#28c4d4", mode: "#b06cff", spawn: "#ff9f45", powerups: "#4caf50" };

// One row: the name small over the value, the drawing large on the right.
function Row({ k, v, open, onPress }: { k: Key; v: RuleView; open: boolean; onPress?: () => void }) {
  const noSpawn = v.pu.length === 0;
  let color = "#8ea3bd", label = "", value = "", art: React.ReactNode = null, icon: React.ReactNode = null;
  if (k === "map") {
    color = SHAPE_C[v.shape]; label = "Map"; icon = <IMap />;
    // just the drawing: the outline and the size (its name is the tooltip)
    const words = `${SHAPE_LABEL[v.shape]} · ${v.size === "auto" ? `Auto (${sizeName(v.sizeKey as any)})` : sizeName(v.sizeKey as any)}`;
    // the size in words, then the outline of the shape
    art = <span className={r.mapArt} title={words} aria-label={words}><span className={r.mapSize}>{sizeName(v.sizeKey as any)}</span><ShapeIcon shape={v.shape} /></span>;
  } else if (k === "respawn") {
    color = v.respawn ? RESPAWN_C.on : RESPAWN_C.off; label = "Respawn"; icon = <IRespawn />; value = v.respawn ? "On" : "Off";
  } else if (k === "win") {
    color = WIN_C[v.win]; label = "Winning condition"; icon = <IWin />;
    value = ""; // (the drawing is the value: "first to 29", the number large)
    art = <span className={r.winArt}><small>{v.win === "score" ? "first to" : "rounds"}</small><b>{v.win === "score" ? v.score : v.rounds}</b></span>;
  } else if (k === "speed") {
    // just the chevrons (one to three lit); the word is their tooltip
    color = SPEED_C[v.speed]; label = "Worm speed"; icon = <ISpeed />; value = ""; art = <span title={SPEEDS[v.speed].label} aria-label={SPEEDS[v.speed].label}><Chevrons n={v.speed} /></span>;
  } else if (k === "spawn") {
    // just the bars (none lit for Off, four for Chaos); the word is their tooltip
    const lvl = noSpawn ? 0 : v.amount;
    color = AMOUNT_C[lvl]; label = "Power-up spawn"; icon = <ISpawn />; value = "";
    art = <span title={AMOUNTS[lvl].label} aria-label={AMOUNTS[lvl].label}><Steps n={lvl} of={4} /></span>;
  } else if (k === "powerups") {
    color = "#19f0b0"; label = "Power-ups"; icon = <IBolt />;
    const on = POWERUPS.filter((p) => v.pu.includes(p.key));
    value = on.length === POWERUPS.length ? `All ${POWERUPS.length}` : on.length === 0 ? "None" : `${on.length} of ${POWERUPS.length}`;
    // every power-up that is on, as the arena's own tokens (red, green, blue coins)
    art = <span className={r.puArt}>{on.map((p) => <PowerUpTokenIcon key={p.key} icon={p.icon} color={p.color} size={26} token />)}</span>;
  } else {
    const f = flavourInfo(v.flavour);
    color = f.color; label = "Game mode"; value = f.name;
    icon = <IMode />; // (the label's icon is the rule's, the same whatever is picked)
    art = null; // (its one-liner is in the choices, not on the row)
  }
  // the rule's name on the left, large; what it is set to on the right (the
  // drawing, then the words), so the value is said once
  const body = (<>
    <span className={r.rowIcon}>{icon}</span>
    <span className={r.rowText}><b>{label}</b></span>
    <span className={r.rowArt}>{art}</span>
    <span className={r.rowValue}>{value}</span>
    {onPress && <span className={`${r.rowChev} ${open ? r.rowChevOpen : ""}`}><Chev /></span>}
  </>);
  // --rc: the value's colour (the right side); --lc: the rule's own (its icon, fixed)
  const style = { "--rc": color, "--lc": LABEL_C[k] } as React.CSSProperties;
  // every row wears its value's colour: a tinted background and border
  if (!onPress) return <div className={`${r.row} ${r.rowStatic} ${r.rowTint}`} style={style}>{body}</div>;
  return <B className={`${r.row} ${r.rowTint} ${open ? r.rowOpen : ""}`} style={style} onClick={onPress} aria-expanded={open}>{body}</B>;
}
// The game modes' icons in the old setup's look (setup/Icon names).
const MODE_ICON: Record<string, string> = { classic: "trophy", pure: "target", chaos: "flame", identity: "users", drink: "beer", fatboy: "burger", duel: "podium", custom: "sliders" };

// The choices a row drops down.
function Choices({ k, rules, setRules, local, touch, minSeats }: { k: Key; rules: Rules; setRules: (fn: (x: Rules) => Rules) => void; local?: boolean; touch?: boolean; minSeats: number }) {
  const set = (p: Partial<Rules>) => setRules((x) => ({ ...x, ...p }));
  const opt = (key: string, on: boolean, color: string, label: React.ReactNode, onClick: () => void, cls = "") => (
    <B key={key} className={`${r.opt} ${on ? r.optOn : ""} ${cls}`} style={{ "--tc": color } as React.CSSProperties} onClick={onClick} aria-pressed={on}>{label}</B>
  );
  if (k === "map") {
    const sizes: Size[] = ["auto", ...MAP_SIZES.map((z) => z.key).filter((x) => !touch || x !== "huge" || rules.size === "huge")];
    const shapeC = SHAPE_C[rules.shape];
    return (<>
      <div className={r.grid} style={{ gridTemplateColumns: "repeat(4, minmax(0, 1fr))" }}>
        {(["rectangle", "stadium", "square", "circle"] as Shape[]).map((sh) => opt(sh, rules.shape === sh, SHAPE_C[sh], <><ShapeIcon shape={sh} /><span>{SHAPE_LABEL[sh]}</span></>, () => set({ shape: sh }), r.optMap))}
      </div>
      <small className={r.sub}>Size</small>
      <div className={r.grid} style={{ gridTemplateColumns: `repeat(${sizes.length}, minmax(0, 1fr))` }}>
        {sizes.map((z) => opt(z, rules.size === z, shapeC, z === "auto" ? `Auto (${sizeName(sizeOf({ ...rules, size: "auto" }))})` : sizeName(z), () => set({ size: z })))}
      </div>
    </>);
  }
  if (k === "win") {
    // Two cards side by side, as the old setup had them: win by score, or play a
    // number of rounds. Each says its number large with − and + either side; the
    // one in use is lit, pressing the other switches to it. Score has the three
    // usual lengths as quick picks under its number.
    const score = scoreOf(rules);
    const card = (kind: "score" | "rounds", title: string, value: number, change: (d: number) => void, extra?: React.ReactNode) => {
      const on = rules.win === kind;
      return (
        <div className={`${r.winCard} ${on ? r.winOn : ""}`} style={{ "--tc": WIN_C[kind] } as React.CSSProperties} onClick={() => { if (!on) set({ win: kind }); }}>
          <span className={r.winHead}><i className={r.radio} />{title}</span>
          <span className={r.winValue}>
            <B onClick={(e) => { e.stopPropagation(); if (!on) set({ win: kind }); change(-1); }} aria-label={`Less: ${title}`}>−</B>
            <b>{value}</b>
            <B onClick={(e) => { e.stopPropagation(); if (!on) set({ win: kind }); change(1); }} aria-label={`More: ${title}`}>+</B>
          </span>
          {extra}
        </div>
      );
    };
    return (
      <div className={r.grid} style={{ gridTemplateColumns: "1fr 1fr" }}>
        {card("score", "Score to win", score, (d) => set({ exact: Math.max(2, Math.min(150, score + d)) }),
          <span className={r.lens}>
            {(["short", "normal", "long"] as Len[]).map((l) => {
              const n = lenScore(rules.players, l, rules.respawn);
              return <B key={l} className={`${r.len} ${rules.win === "score" && score === n ? r.lenOn : ""}`} onClick={(e) => { e.stopPropagation(); set({ win: "score", length: l, exact: null }); }}>{LEN_LABEL[l]} <em>{n}</em></B>;
            })}
          </span>)}
        {card("rounds", "Rounds to play", rules.rounds, (d) => set({ rounds: Math.max(1, Math.min(30, rules.rounds + d)) }),
          <span className={r.lens}>
            {(["short", "normal", "long"] as Len[]).map((l) => {
              const n = roundLen(l, rules.respawn);
              return <B key={l} className={`${r.len} ${rules.win === "rounds" && rules.rounds === n ? r.lenOn : ""}`} onClick={(e) => { e.stopPropagation(); set({ win: "rounds", rounds: n }); }}>{LEN_LABEL[l]} <em>{n}</em></B>;
            })}
          </span>)}
      </div>
    );
  }
  if (k === "respawn") {
    // a round's length changes with it, so the rounds go back to its usual number
    const pick = (on: boolean) => set({ respawn: on, exact: null, rounds: roundLen("normal", on) });
    return <div className={r.grid} style={{ gridTemplateColumns: "1fr 1fr" }}>{opt("on", rules.respawn, RESPAWN_C.on, "On", () => pick(true))}{opt("off", !rules.respawn, RESPAWN_C.off, "Off", () => pick(false))}</div>;
  }
  if (k === "speed") return <div className={r.grid} style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}>{SPEEDS.map((x, i) => opt(x.label, rules.speed === i, SPEED_C[i], x.label, () => set({ speed: i as Tri })))}</div>;
  if (k === "spawn") {
    const lvl = rules.pu.length ? rules.amount : 0;
    return <div className={r.grid} style={{ gridTemplateColumns: "repeat(5, minmax(0, 1fr))" }}>{AMOUNTS.map((x, i) => opt(x.label, lvl === i, AMOUNT_C[i], x.label, () => set({ amount: i as Spawn, pu: i > 0 && !rules.pu.length ? START.pu.slice() : rules.pu })))}</div>;
  }
  if (k === "mode") {
    const now = flavourOf(rules);
    // the named modes (no Quick duel here: a set-up like that may come back as a preset)
    const ids: FlavourId[] = MODES.filter((m) => m.id !== "custom").map((m) => m.id as FlavourId);
    return (
      <div className={r.grid} style={{ gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}>
        {ids.map((id) => { const f = flavourInfo(id); return opt(id, now === id, f.color, <><span className={r.modeIcon}><Icon name={MODE_ICON[id]} size={18} color={f.color} /></span><span className={r.modeText}><b>{f.name}</b><small>{f.line}</small></span></>, () => setRules((x) => applyFlavour(x, id, local)), r.optMode); })}
      </div>
    );
  }
  // power-ups: the quick sets, then each one on or off
  const toggle = (key: string) => set({ pu: rules.pu.includes(key) ? rules.pu.filter((x) => x !== key) : [...rules.pu, key] });
  return (<>
    <div className={r.sets}>{POWERUP_SETS.map((x) => opt(x.name, isSet(rules, x.pu), "#19f0b0", x.name, () => set({ pu: x.pu.slice(), amount: x.amount })))}</div>
    <div className={r.grid} style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}>
      {POWERUPS.map((p) => {
        const on = rules.pu.includes(p.key);
        return <B key={p.key} className={`${r.pu} ${r["pu" + p.color[0].toUpperCase() + p.color.slice(1)]} ${on ? "" : r.puOff}`} onClick={() => toggle(p.key)} aria-pressed={on}><PowerUpTokenIcon icon={p.icon} color={p.color} size={22} dim={!on} /><span>{p.name}</span></B>;
      })}
    </div>
  </>);
}
function Step({ value, onChange }: { value: number; onChange: (d: number) => void }) {
  return <span className={r.step}><B onClick={() => onChange(-1)} aria-label="Less">−</B><b>{value}</b><B onClick={() => onChange(1)} aria-label="More">+</B></span>;
}

// A drop-down that closes on a press anywhere outside it (and on Escape).
export function useOutside(open: boolean, close: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  const closeRef = useRef(close);
  closeRef.current = close;
  useEffect(() => {
    if (!open) return;
    const down = (e: PointerEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) closeRef.current(); };
    const key = (e: KeyboardEvent) => { if (e.key === "Escape") closeRef.current(); };
    document.addEventListener("pointerdown", down, true);
    document.addEventListener("keydown", key);
    return () => { document.removeEventListener("pointerdown", down, true); document.removeEventListener("keydown", key); };
  }, [open]);
  return ref;
}

const ROWS: Key[] = ["map", "respawn", "win", "speed", "mode", "spawn", "powerups"];
const SINGLE: Key[] = ["respawn", "speed", "spawn", "mode"]; // one press is the choice: the drop-down closes

// The rows. With `rules` + `setRules` (the host, a local game) they open; with
// only `view` (a guest) they are read only.
export function RuleRows({ rules, setRules, view, local, touch, minSeats = 2 }: {
  rules?: Rules; setRules?: (fn: (x: Rules) => Rules) => void; view?: RuleView; local?: boolean; touch?: boolean; minSeats?: number;
}) {
  const [open, setOpen] = useState<Key | null>(null);
  const v = rules ? viewOfRules(rules) : view!;
  return (
    <div className={r.rows}>
      {ROWS.map((k) => <RowWithChoices key={k} k={k} v={v} open={open === k} setOpen={setOpen} rules={rules} setRules={setRules} local={local} touch={touch} minSeats={minSeats} />)}
    </div>
  );
}
function RowWithChoices({ k, v, open, setOpen, rules, setRules, local, touch, minSeats }: {
  k: Key; v: RuleView; open: boolean; setOpen: (k: Key | null) => void; rules?: Rules; setRules?: (fn: (x: Rules) => Rules) => void; local?: boolean; touch?: boolean; minSeats: number;
}) {
  const ref = useOutside(open, () => setOpen(null));
  const editable = !!rules && !!setRules;
  const wrapped = editable && SINGLE.includes(k)
    ? (fn: (x: Rules) => Rules) => { setRules!(fn); setOpen(null); }
    : setRules;
  return (
    <div className={r.anchor} ref={ref}>
      <Row k={k} v={v} open={open} onPress={editable ? () => setOpen(open ? null : k) : undefined} />
      {open && editable && (
        <div className={`${r.pop} ${k === "powerups" || k === "mode" || k === "map" ? r.popWide : ""}`}>
          <Choices k={k} rules={rules!} setRules={wrapped!} local={local} touch={touch} minSeats={minSeats} />
        </div>
      )}
    </div>
  );
}
